import React from "react";
import { BsFileText } from "react-icons/bs";

import { AttachmentTable } from "src/common/components/AttachmentTable";
import { DataTable } from "src/common/components/DataTable";
import { Popover } from "src/common/components/Popover";
import { Table } from "src/common/components/Table";
import { Timeline, TimelineItemValue } from "src/common/components/Timeline";
import {
	GroupedRecordList,
	GroupedRecordListGroup,
	Record,
	RecordGroup,
	RecordGroupContainer,
	RecordList,
	RecordListItem,
	RecordTableContainer
} from "src/modules/shared/dashboard/components/Record";
import { Section } from "src/modules/shared/dashboard/components/Section";
import {
	GroupContentItem,
	GroupDescriptor,
	GroupedListDescriptor,
	GroupListDescriptor,
	ListDescriptor,
	SectionContentItem,
	SectionDescriptor,
	TableDescriptor,
	TableGroupListDescriptor
} from "src/modules/shared/dashboard/types";

interface SectionListProps {
	sections: SectionDescriptor[];
}

export const SectionList: React.FunctionComponent<SectionListProps> = ({
	sections
}) => (
	<>
		{sections.map((section, i) => (
			<SectionRenderer key={i} {...section} />
		))}
	</>
);

const SectionRenderer: React.FunctionComponent<SectionDescriptor> = ({
	label,
	attachments = [],
	content
}) => (
	<Section title={label} attachments={attachments}>
		{content.map((item, i) => (
			<SectionContentItemRenderer key={i} {...item} />
		))}
	</Section>
);

const SectionContentItemRenderer: React.FunctionComponent<
	SectionContentItem
> = item => {
	if (item.type === "group-list") {
		return <GroupListRenderer {...item} />;
	}
	if (item.type === "group") {
		return <GroupRenderer {...item} />;
	}
	if (item.type === "table") {
		return <TableRenderer {...item} />;
	}
	if (item.type === "table-group-list") {
		return <TableGroupListRenderer {...item} />;
	}
	if (item.type === "grouped-list") {
		return <GroupedListRenderer {...item} />;
	}
	if (item.type === "list") {
		return <ListRenderer {...item} />;
	}
	if (item.type === "record") {
		return <Record {...item} />;
	}
	if (item.type === "timeline") {
		return (
			<Timeline
				items={item.items}
				renderActions={({ attachments }) => {
					if (attachments.length === 0) {
						return null;
					}
					return (
						<Popover
							target={<BsFileText />}
							content={
								<AttachmentTable attachments={attachments} />
							}
						/>
					);
				}}
				renderContent={({ data }) => {
					if (Array.isArray(data)) {
						return data.map((line, i) => (
							<TimelineItemValue key={i} value={line} />
						));
					}
					return <TimelineItemValue value={data} />;
				}}
			/>
		);
	}
	return null;
};

const GroupListRenderer: React.FunctionComponent<GroupListDescriptor> = ({
	groups
}) => (
	<RecordGroupContainer>
		{groups.map((group, i) => (
			<GroupRenderer key={i} {...group} />
		))}
	</RecordGroupContainer>
);

const GroupRenderer: React.FunctionComponent<GroupDescriptor> = ({
	variant,
	label,
	content
}) => (
	<RecordGroup variant={variant} label={label}>
		{content.map((item, i) => (
			<GroupContentItemRenderer key={i} {...item} />
		))}
	</RecordGroup>
);

const GroupContentItemRenderer: React.FunctionComponent<
	GroupContentItem
> = item => {
	if (item.type === "table") {
		return <TableRenderer {...item} />;
	}
	if (item.type === "table-group-list") {
		return <TableGroupListRenderer {...item} />;
	}
	if (item.type === "grouped-list") {
		return <GroupedListRenderer {...item} />;
	}
	if (item.type === "list") {
		return <ListRenderer {...item} />;
	}
	if (item.type === "record") {
		return <Record {...item} />;
	}
	return null;
};

const TableRenderer: React.FunctionComponent<TableDescriptor> = ({
	label,
	attachments,
	columns,
	rows,
	timestamp,
	sid,
	origin
}) => {
	const dataTableColumns = React.useMemo(
		() => columns.map((label, i) => ({ key: `${i}`, label })),
		[columns]
	);
	const dataTableRows = React.useMemo(
		() =>
			rows.map(values => {
				const map: { [key: string]: string } = {};
				for (let i = 0; i < values.length; i++) {
					map[`${i}`] = values[i];
				}
				return map;
			}),
		[rows]
	);
	return (
		<RecordTableContainer
			label={label}
			attachments={attachments}
			timestamp={timestamp}
			sid={sid}
			origin={origin}
		>
			<DataTable columns={dataTableColumns} data={dataTableRows} />
		</RecordTableContainer>
	);
};

const TableGroupListRenderer: React.FunctionComponent<
	TableGroupListDescriptor
> = ({ label, attachments, columns, groups, timestamp, sid, origin }) => (
	<RecordTableContainer
		label={label}
		attachments={attachments}
		timestamp={timestamp}
		sid={sid}
		origin={origin}
	>
		<Table>
			<thead>
				<tr>
					{columns.map((label, i) => (
						<th key={i}>{label}</th>
					))}
				</tr>
			</thead>
			<tbody>
				{groups.map(({ label, rows }, i1) => (
					<React.Fragment key={i1}>
						{label && (
							<tr className="no-border">
								<td
									colSpan={
										rows.length === 0 ? 0 : rows[0].length
									}
								>
									<b>{label}</b>
								</td>
							</tr>
						)}
						{rows.map((values, i2) => (
							<tr key={i2}>
								{values.map((value, i3) => (
									<td key={i3}>{value}</td>
								))}
							</tr>
						))}
					</React.Fragment>
				))}
			</tbody>
		</Table>
	</RecordTableContainer>
);

const GroupedListRenderer: React.FunctionComponent<GroupedListDescriptor> = ({
	attachments,
	groups,
	timestamp,
	sid,
	origin
}) => (
	<GroupedRecordList timestamp={timestamp} sid={sid} origin={origin}>
		{groups.map(({ label, items }, i) => (
			<GroupedRecordListGroup
				key={i}
				label={label}
				attachments={i === 0 ? attachments : []}
			>
				{items.map(({ value, ...item }, i) => (
					<RecordListItem
						key={i}
						{...item}
						value={
							value && (
								<div
									style={{
										display: "flex",
										flexDirection: "column"
									}}
								>
									{value.split("\n").map((item, i) => (
										<div key={i}>{item}</div>
									))}
								</div>
							)
						}
					/>
				))}
			</GroupedRecordListGroup>
		))}
	</GroupedRecordList>
);

const ListRenderer: React.FunctionComponent<ListDescriptor> = ({
	label,
	attachments,
	items,
	timestamp,
	sid,
	origin
}) => (
	<RecordList
		label={label}
		attachments={attachments}
		timestamp={timestamp}
		sid={sid}
		origin={origin}
	>
		{items.map(({ value, ...item }, i) => (
			<RecordListItem
				key={i}
				{...item}
				value={
					value && (
						<div
							style={{ display: "flex", flexDirection: "column" }}
						>
							{value.split("\n").map((item, i) => (
								<div key={i}>{item}</div>
							))}
						</div>
					)
				}
			/>
		))}
	</RecordList>
);
